import {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import BosService from "../../../../services/web/bos-service.js";
import CorporationService from "../../../../services/web/corporation-service.js";
import {getDashboardPermissions} from "../utils/dashboard-permissions.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import dashboardDispatchService from "../../../../services/web/dashboard-dispatch-service.js";

const DEFAULT_DASHBOARD_CONFIGURATION = {
    boName: "BO",
    entityCity: "",
    entityLatitude: "",
    entityLongitude: "",
};

const normalizeBoName = value => {
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value?.boName === "string" && value.boName.trim()) return value.boName;
    if (typeof value?.bo_name === "string" && value.bo_name.trim()) return value.bo_name;
    return DEFAULT_DASHBOARD_CONFIGURATION.boName;
};

const normalizeCoordinate = value => value ?? "";

const useDashboardConfiguration = () => {
    const hasPermission = useHasPermission();
    const permissions = useMemo(() => getDashboardPermissions(hasPermission), [hasPermission]);

    const boNameQuery = useQuery({
        queryFn: () => BosService.getBoName(),
        queryKey: DASHBOARD_QUERY_KEYS.boName(),
        staleTime: 30 * 60 * 1000,
    });

    const corporationQuery = useQuery({
        queryFn: () => CorporationService.entity(),
        queryKey: DASHBOARD_QUERY_KEYS.corporation(),
        staleTime: 30 * 60 * 1000,
    });

    const setupQuery = useQuery({
        queryFn: () => dashboardDispatchService.setup(),
        queryKey: DASHBOARD_QUERY_KEYS.setup(),
        staleTime: 30 * 60 * 1000,
    });

    const configuration = useMemo(() => {
        const entity = corporationQuery.data ?? {};
        const setup = setupQuery.data ?? {};

        return {
            boName: normalizeBoName(boNameQuery.data),
            offSet: setup.offSet,
            isSuggestKm: Boolean(setup.isSuggestKm),
            entityCity: entity.city ?? DEFAULT_DASHBOARD_CONFIGURATION.entityCity,
            entityLatitude: normalizeCoordinate(entity.latitude),
            entityLongitude: normalizeCoordinate(entity.longitude),
        };
    }, [boNameQuery.data, corporationQuery.data, setupQuery.data]);

    return {
        configuration,
        permissions,
        isLoading: boNameQuery.isLoading || corporationQuery.isLoading,
        isError: boNameQuery.isError || corporationQuery.isError,
    };
};

export default useDashboardConfiguration;
